import { useUserInfo } from "@/api/user";
import { config } from "@/config";
import { LDContext, LDProvider } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

export const flagValues = {
  onboarding: "onboarding",
  audioTranscription: "audioTranscription",
  billing: "billing",
  notionLikeEditor: "notionLikeEditor",
  maintenanceMode: "maintenanceMode",
  extremeReasoningEffort: "extremeReasoningEffort",
  // LD key: notifications — per-org gate for the Liveblocks notifications feature
  // (the bell + tool-confirmation auto-delete). Matches the backend flag key.
  notifications: "notifications",
  // LD key: automations — per-org gate for the Automations UI (rail icon,
  // panel, detail pane, create). The agent-side FlowSchedule tools are gated
  // separately by the Automation_Internal tool-set assignment in the backend.
  automations: "automations",
  // LD key: live-transcription — per-org gate for streaming dictation over
  // the realtime transcription bridge (gpt-live-transcribe). Off or absent
  // falls back to the legacy HTTP transcription path. Also gates the mobile
  // voice v2 screen (MobileVoiceGate). Matches the backend flag key.
  liveTranscription: "liveTranscription",
  // LD key: harness-runtime — the same gate the backend spends at session create. The
  // model-provider surface hangs off it because a credential connected while turns still run
  // through the ChatCompletion loop is one nothing would ever spend, and so does the composer's
  // harness selector: off, Cohesive is the only choice and there is nothing to select.
  harnessRuntime: "harnessRuntime",
  // LD key: canvas-comments — per-org gate for canvas comments (pins, thread
  // windows, the rail section, the entry points). Matches the backend flag key,
  // which also gates the comment scopes on the Liveblocks token.
  canvasComments: "canvasComments",
} as const;

export type Flags = {
  [K in keyof typeof flagValues]: boolean;
};

export function LaunchDarklyProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: userInfo } = useUserInfo();

  const launchDarklyContext = useMemo(() => {
    if (userInfo) {
      const context: LDContext = {
        kind: "multi",
        user: {
          key: userInfo?.user?.id,
          name: userInfo?.user?.name,
          email: userInfo?.user?.email,
          created_at: userInfo?.user?.created_at,
        },
      };

      if (userInfo?.organization) {
        context.organization = {
          key: userInfo?.organization?.id,
          name: userInfo?.organization?.name,
        };
      }

      return context;
    }

    return undefined;
  }, [userInfo]);

  const relayHost = config.launchDarkly.relayHost;

  return (
    <LDProvider
      clientSideID={config.launchDarkly.clientSideId}
      context={launchDarklyContext}
      deferInitialization
      options={
        relayHost
          ? {
              baseUrl: relayHost,
              streamUrl: relayHost,
              eventsUrl: relayHost,
            }
          : undefined
      }
    >
      {children}
    </LDProvider>
  );
}
